import { usePersonGetSimplePerson } from "@/orval/gen"
import {
  Combobox,
  ComboboxItem,
  Loader,
  SelectProps,
  ScrollArea,
  useCombobox,
  InputBase,
  Input,
} from "@mantine/core"
import { useTranslation } from "react-i18next"

export interface DefaultSingleSelectProps
  extends Omit<SelectProps, "renderOption"> {
  options: ComboboxItem[]
  loading?: boolean
  onSelectionChange?: (selectedValues: string | null) => void
  onSearchChange?: (searchValue: string) => void
  renderOption?: (item: ComboboxItem) => JSX.Element
  onScrolledToBottom?: () => void
  noOptionsFoundText?: string
}

export function DefaultSingleSelect({
  options,
  loading,
  searchValue,
  onSelectionChange,
  onScrolledToBottom,
  onSearchChange,
  renderOption,
  label,
  value,
  placeholder,
  noOptionsFoundText,
}: DefaultSingleSelectProps) {
  const [internalSearchValue, setinternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(
    () => onSearchChange && onSearchChange(internalSearchValue),
    [internalSearchValue]
  )

  React.useEffect(() => {
    setinternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const [selectedValues, setSelectedValues] = React.useState<string | null>(
    null
  )

  React.useEffect(() => {
    onSelectionChange && onSelectionChange(selectedValues)
  }, [selectedValues])

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => {
      combobox.focusSearchInput()
    },
  })

  const handleValueSelect = (val: string) => {
    setSelectedValues(val)
    setinternalSearchValue("")
    combobox.closeDropdown()
  }

  const handleValueRemove = (_: string) => setSelectedValues(null)

  const viewportRef = React.useRef<HTMLDivElement>(null)

  const { t } = useTranslation("app_server")

  const personId = Number(value)
  const isValidId = !!value && !isNaN(personId) && personId > 0
  const { data, isError } = usePersonGetSimplePerson(
    isValidId ? { personId } : undefined,
    { query: { enabled: isValidId } }
  )
  const selectedOption = {
    value: value,
    label: !isError && data?.name ? data.name : value,
  }

  return (
    <Combobox onOptionSubmit={handleValueSelect} store={combobox}>
      <Combobox.Target>
        <InputBase
          label={label}
          component="button"
          type="button"
          pointer
          rightSection={
            value ? (
              <Combobox.ClearButton onClear={() => handleValueRemove(value)} />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={value ? undefined : "none"}
          onClick={() => combobox.toggleDropdown()}
          placeholder={placeholder}
          style={{
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            display: "block",
          }}
        >
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {selectedOption?.label || value || (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
          </span>
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <Combobox.Search
            value={internalSearchValue}
            onChange={(event) =>
              setinternalSearchValue(event.currentTarget.value)
            }
            placeholder={placeholder}
          />
          <ScrollArea
            viewportRef={viewportRef}
            style={{ height: 300 }}
            onScrollPositionChange={() => {
              const scrollArea = viewportRef.current
              if (
                scrollArea &&
                scrollArea.scrollHeight -
                  scrollArea.scrollTop -
                  scrollArea.clientHeight <
                  10 &&
                onScrolledToBottom
              ) {
                onScrolledToBottom()
              }
            }}
          >
            {options.length === 0 ? (
              <Combobox.Empty>
                {noOptionsFoundText || t("NoOptionsFound")}
              </Combobox.Empty>
            ) : (
              options.map((option) => {
                return renderOption ? (
                  renderOption(option)
                ) : (
                  <Combobox.Option
                    {...option}
                    key={option.value}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {option.label}
                    </span>
                  </Combobox.Option>
                )
              })
            )}
            {loading && (
              <div style={{ padding: 8 }}>
                <Loader size="xs" />
              </div>
            )}
          </ScrollArea>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
